<template>
  <div>
    <Panel
      :icon="['fas', 'bullseye']"
      loader-text="Loading Ring Tree Target Requests"
      :loading="loading"
      :show-loading-error="showLoadingError"
      :loading-error-text="loadingErrorText"
    >
      <template v-slot:header>
        <span data-cy="heading">Ring Tree Target Requests for <strong>{{ sellerOfferName }}</strong></span>
      </template>

      <template v-slot:content>
        <b-table
          data-cy="table-ring-tree-target-requests"
          :tbody-tr-attr="{ 'data-cy': 'table-row' }"
          :items="filteredRequests"
          :fields="fields"
          primary-key="id"
          show-empty
          responsive
        >
          <template #empty>
            <i><span class="text-muted">No Ring Tree Target Requests for this offer</span></i>
          </template>
          
          <template #cell(status)="row">
            <b-badge :variant="getBadgeVariantForStatus(row.item.status)">
              {{ row.item.status }}
            </b-badge>
          </template>

          <template #cell(thirdParty)="row">
            {{ row.item.thirdParty ? 'Yes' : 'No' }}
          </template>

          <template #cell(createRingTree)="row">
            {{ row.item.createRingTree ? 'Yes' : 'No' }}
          </template>

          <template #cell(createdAt)="row">
            {{ dateFormat(row.item.createdAt, 'MMM DD, YYYY') }}
          </template>

          <template #cell(actions)="row">
            <div class="d-flex gap-2">
              <b-button
                v-if="row.item.status === 'Completed'"
                variant="primary"
                size="sm"
                title="View Ring Tree Target"
                data-cy="button-view-target"
                :href="getTargetUrl(row.item)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <font-awesome-icon
                  :icon="['fas', 'external-link-alt']"
                  size="sm"
                  class="mr-2"
                />
                View Target
              </b-button>
              
              <b-button
                v-if="row.item.status === 'Completed' && row.item.rtId"
                variant="primary"
                size="sm"
                title="View Ring Tree"
                data-cy="button-view-ring-tree"
                :href="getRingTreeUrl(row.item)"
                target="_blank"
                rel="noopener noreferrer"
                class="ml-3"
              >
                <font-awesome-icon
                  :icon="['fas', 'external-link-alt']"
                  size="sm"
                  class="mr-2"
                />
                View Ring Tree
              </b-button>
            </div>
          </template>
        </b-table>
      </template>
    </Panel>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import { showErrorMessage } from '@/notification-utils'

export default {
  name: 'ListRingTreeTargetRequests',

  title: 'Ring Tree Target Requests',

  mixins: [dateFormatMixin],

  props: {
    sellerOfferRegistrationId: {
      type: String,
      required: true
    },
    sellerOfferId: {
      type: String,
      required: true
    },
  },

  data() {
    return {
      loading: true,
      loadingOfferDetails: false,
      showLoadingError: false,
      loadingErrorText: ''
    }
  },

  computed: {
    ...mapState('ringTreeTargetRequest', [
      'requests'
    ]),
    
    ...mapGetters('sellerOffer', ['allOffersCombined']),

    selectedOfferRegistration() {
      return this.allOffersCombined.find(
        offer => offer.id === this.sellerOfferRegistrationId
      )
    },

    sellerOfferName() {
      return this.selectedOfferRegistration?.sellerOffer?.name || ''
    },

    isMedicareCategory() {
      return this.selectedOfferRegistration?.sellerOffer?.category?.isMedicare || false
    },

    filteredRequests() {
      return this.requests.filter(request => 
        request.sellerOfferRegistrationId === this.sellerOfferRegistrationId
      )
    },

    fields() {
      const baseFields = [
        {
          key: 'status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          key: 'trafficSourceSubId',
          label: 'Traffic Source Sub ID',
          sortable: true,
        },
        {
          key: 'thirdParty',
          label: 'Third Party',
          sortable: true,
        },
        {
          key: 'createRingTree',
          label: 'Create Ring Tree',
          sortable: true,
        }
      ]

      if (this.isMedicareCategory) {
        baseFields.push({
          key: 'creativeId',
          label: 'Creative ID',
          sortable: true,
        })
      }

      baseFields.push(
        {
          key: 'createdAt',
          label: 'Requested',
          sortable: true,
        },
        {
          key: 'actions',
          label: 'Actions',
          tdAttr: {
            'data-cy': 'table-column-actions'
          }
        }
      )

      return baseFields
    }
  },

  async created() {
    await this.loadOfferDetails()
    await this.loadRingTreeTargetRequests()
  },

  methods: {
    ...mapActions('ringTreeTargetRequest', [
      'fetchAllRequests'
    ]),
    
    ...mapActions('sellerOffer', [
      'getSellerAvailableOffers', 
      'getSellerAppliedOffers'
    ]),

    async loadOfferDetails() {
      try {
        this.loadingOfferDetails = true
        await Promise.all([
          this.getSellerAvailableOffers(),
          this.getSellerAppliedOffers()
        ])
      } catch (error) {
        // Continue even if offer details fail to load
        console.error('Failed to load offer details:', error)
      } finally {
        this.loadingOfferDetails = false
      }
    },

    async loadRingTreeTargetRequests() {
      try {
        this.loading = true
        await this.fetchAllRequests()
      } catch (error) {
        this.showLoadingError = true
        this.loadingErrorText = error.response?.data?.title || 'Failed to load Ring Tree Target requests'
        showErrorMessage(this.loadingErrorText)
      } finally {
        this.loading = false
      }
    },

    getBadgeVariantForStatus(status) {
      const statusToVariantMap = {
        'pending': 'warning',
        'approved': 'success',
        'rejected': 'danger',
        'processing': 'info',
        'completed': 'success',
        'failed': 'danger'
      }

      return statusToVariantMap[status.toLowerCase()] || 'secondary'
    },

    getTargetUrl(request) {
      return `https://app.ringba.com/#/dashboard/ringtreetarget/${request.rttId}`
    },

    getRingTreeUrl(request) {
      return `https://app.ringba.com/#/dashboard/ringtree/${request.rtId}`
    }
  }
}
</script>

<style lang="scss" scoped>
</style>